/**
 * Whose visits the Today worklist asks for (PROD-3045 N1).
 *
 * THE DEFECT. The worklist always sent `practitioners: [<signed-in user>]`. For a physician that is
 * right: Flutter sends `[userData.id]`. For a nurse it is wrong. The appointment search matches a
 * visit when one of its PARTICIPANTS is in the list (Scrips.AppointmentScheduling
 * `AppointmentRepository.cs:375-376`, `Participants.Any(i => model.Practitioners.Contains(i.Reference))`),
 * and a nurse is not a participant of the physician's visit. So a nurse's Today read
 * "No appointments today" while a visit she supports was in session (measured on dev, 6 Oct 18:50).
 *
 * THE RULE, from Flutter's Today list (`appointment_list_widget.dart:126-133`):
 *   - physician → `[userData.id]`;
 *   - nurse     → `nurseAssignedProviders`, the ids `POST /api/Staff/AssignedProviders` returns for
 *                 the selected practice and today (server-side: the providers of her active
 *                 specialities, `StaffSetupService.GetAssignedProviders`).
 * The Calendar already follows it (`calendar-page.tsx`, `practitionerIds: assignedProviderIds`).
 *
 * TWO CHOICES THAT ARE NOT FLUTTER'S DEFAULTS, both deliberate:
 *   - `null` while the nurse's list is still loading. The page then sends NOTHING. An empty
 *     `practitioners` array means "no provider filter" to the server, so asking before the list
 *     arrives would flash the whole practice's day and then shrink it.
 *   - an EMPTY assigned list stays empty (no fallback to the nurse herself). That is Flutter's
 *     request and the scoping guard's rule (`provider-scoping.guard.test.ts`): the server answers it
 *     with every visit in the practice. Falling back to her own id would bring back this defect.
 */
export type WorklistProviderScope = { practitionerId: string } | { practitionerIds: string[] };

export function worklistProviderScope(args: {
  isNurse: boolean;
  /** The signed-in user's own provider id (`user.practitionerId ?? user.id`). */
  selfId: string | null | undefined;
  /** The nurse's assigned provider ids, `undefined` until the request resolves. */
  assignedProviderIds: readonly string[] | null | undefined;
}): WorklistProviderScope | null {
  if (args.isNurse) {
    return args.assignedProviderIds ? { practitionerIds: [...args.assignedProviderIds] } : null;
  }
  return args.selfId ? { practitionerId: args.selfId } : null;
}
